import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { alpha, darken, lighten, rgbToHex } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import { type CSSProperties, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserSettings as setUserSettingsProps } from '../_flux/Actions';
import { type FluxStore } from '../_flux/Store';
import { type UserSettings } from '../login/EnsureLoggedInContainer';
import { type CustomTheme } from '../provider/ThemeProvider';
import { setUserSettings } from './AxiosVars';
import { setConf } from './Session';
import Toast from './Toast';
import { checkUndefinedOrNull } from './Tools';

type SpeechbubbleProps = {
    anchor?: string;
    autoClose?: number;
    color?: string;
    crisp?: boolean;
    disabled?: boolean;
    error?: boolean;
    id?: string;
    info?: boolean;
    onClick?: () => void;
    origin?: 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-end' | 'top-start' | 'top';
    primary?: string;
    secondary?: string;
    style?: CSSProperties;
    success?: boolean;
    title?: string;
    warning?: boolean;
    zIndex?: number;
};

const Speechbubble = (moduleProps: SpeechbubbleProps) => {
    const userSettings = useSelector((state: FluxStore) => state.userSettings);
    const dispatch = useDispatch();

    const props = {
        crisp: false,
        disabled: false,
        error: false,
        info: false,
        origin: 'top' as SpeechbubbleProps['origin'],
        style: {},
        success: false,
        warning: false,
        ...moduleProps,
    };
    const [anchor, setAnchor] = useState<HTMLElement>(undefined);
    const [inFade, setInFade] = useState<boolean>(false);
    const [openPopper, setOpenPopper] = useState<boolean>(false);

    const theme: CustomTheme = useTheme();
    const color = props.color || theme?.palette?.primary?.main;

    const saveToUserSettings = (array) => {
        const mainSettings = {
            ...userSettings?.main,
            hiddenBubbles: array,
        };
        const newUserSettings: UserSettings = {
            ...userSettings,
            main: mainSettings,
        };

        setUserSettings(newUserSettings)
            .then(() => {
                dispatch(setUserSettingsProps(newUserSettings));
                setConf(newUserSettings);
            })
            .catch(() => Toast.addError({ key: 'Speechbubble_setUserSettings', primary: 'Fehler beim Ändern des Anzeigemodus', secondary: 'Bitte versuchen Sie es in ein paar Sekunden erneut' }));
    };

    const close = () => {
        if (props.id) {
            const newHidden = Object.assign([], userSettings && userSettings?.main && userSettings?.main?.hiddenBubbles);
            newHidden.push(props.id);
            setInFade(false);
            setTimeout(() => {
                setOpenPopper(false);
                saveToUserSettings(newHidden);
            }, 500);
        } else setOpenPopper(false);
    };

    useEffect(function () {
        let timer = undefined;
        let timer2 = undefined;
        timer = setTimeout(() => {
            const newAnchor: HTMLElement = document.querySelector(props.anchor);
            setAnchor(newAnchor);
            setOpenPopper(true);
            setInFade(true);
        }, 500);
        if (props.autoClose) {
            timer2 = setTimeout(() => {
                setInFade(false);
                setTimeout(() => {
                    setOpenPopper(false);
                }, 500);
            }, props.autoClose + 500);
        }

        return function cleanup() {
            clearTimeout(timer);
            clearTimeout(timer2);
        };
    }, []);

    const paperStyle = {
        background: 'linear-gradient(60deg, ' + rgbToHex(lighten(color, 0.1)) + ', ' + rgbToHex(darken(color, 0.1)) + ')',
        color: '#fff',
        fontSize: '0.875rem',
        padding: 8,
        margin: props.origin.startsWith('left') || props.origin.startsWith('right') ? '0 20px' : '20px 0',
        ...props.style,
    };
    const paperAfterStyle: CSSProperties = {
        content: '""',
        position: 'absolute',
        width: 0,
        height: 0,
        borderTop: '20px solid ' + color,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderBottom: 0,
        zIndex: -1,
        transform: 'rotate(' + (props.origin.startsWith('bottom') ? 180 : props.origin.startsWith('left') ? 270 : props.origin.startsWith('right') ? 90 : 0) + 'deg)',
        marginLeft: props.origin.startsWith('top') || props.origin.startsWith('bottom') ? -7.5 : undefined,
        top: props.origin.startsWith('bottom')
            ? 3
            : props.origin.startsWith('left') || props.origin.startsWith('right')
              ? props.origin.endsWith('start')
                  ? 5
                  : props.origin.endsWith('end')
                    ? undefined
                    : 'calc(50% - 10px)'
              : undefined,
        bottom: props.origin.startsWith('top') ? 3 : props.origin.startsWith('left') || props.origin.startsWith('right') ? (props.origin.endsWith('end') ? 5 : undefined) : undefined,
        left: props.origin.startsWith('right')
            ? 3
            : props.origin.startsWith('top') || props.origin.startsWith('bottom')
              ? props.origin.endsWith('start')
                  ? 10
                  : props.origin.endsWith('end')
                    ? undefined
                    : 'calc(50% - 5px)'
              : undefined,
        right: props.origin.startsWith('left') ? 3 : props.origin.startsWith('top') || props.origin.startsWith('bottom') ? (props.origin.endsWith('end') ? 5 : undefined) : undefined,
    };
    const paperShadowStyle = {
        boxShadow:
            '0px 3px 5px -1px ' +
            alpha(rgbToHex(darken(color, 0.2)), 0.2) +
            ', 0px 6px 10px 0px ' +
            alpha(rgbToHex(darken(color, 0.2)), 0.14) +
            ', 0px 1px 18px 0px ' +
            alpha(rgbToHex(darken(color, 0.2)), 0.12) +
            '',
    };

    const hiddenBubbles = userSettings && userSettings?.main && userSettings?.main?.hiddenBubbles;

    return (
        <Popper
            anchorEl={anchor}
            placement={props.origin}
            open={openPopper}
            onClick={props.disabled ? undefined : props.onClick ? props.onClick : close}
            style={{ cursor: props.disabled ? undefined : 'pointer', zIndex: !checkUndefinedOrNull(props.zIndex) ? props.zIndex : 1300 }}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} in={((props.id && (!hiddenBubbles || (hiddenBubbles && !hiddenBubbles.includes(props.id)))) || !props.id) && inFade} timeout={500}>
                    <Paper style={{ ...paperStyle, ...paperShadowStyle, lineHeight: 1 }}>
                        {props.title && (
                            <Typography variant="h6" style={{ marginTop: 0, marginBottom: props.primary ? 8 : 0, lineHeight: 1 }}>
                                {props.title}
                            </Typography>
                        )}
                        {props.primary}
                        {props.secondary}
                        {}
                        <div style={paperAfterStyle} />
                    </Paper>
                </Fade>
            )}
        </Popper>
    );
};

export default Speechbubble;
